/*
 * Договор обмена по WebSocket.
 *
 * Один и тот же файл читают и браузер, и сервер — поэтому расхождение
 * протокола между ними ловит компилятор, а не пользователь на проде.
 */
import { z } from 'zod';
/* --- Клиент → сервер ---------------------------------------------------- */
const identifierSchema = z.string().trim().min(1).max(128);
const envelopeBase = {
    id: identifierSchema,
    ts: z.number().finite().nonnegative()
};
/** Максимальная длина обычного сообщения и подписи к вложению. */
export const MESSAGE_TEXT_LIMIT = 4_000;
/** Сколько вложений несёт одно сообщение. Клиент режет выбор по этому же числу. */
export const MESSAGE_ATTACHMENT_LIMIT = 10;
/** «Один просмотр» числом не выражается, поэтому перечисление, а не секунды. */
export const ephemeralMediaSchema = z.enum(['once', 'seconds-3', 'seconds-10', 'seconds-30']);
/** Сколько секунд показывать. У «одного просмотра» — жёсткий потолок. */
export const EPHEMERAL_SECONDS = {
    once: 60,
    'seconds-3': 3,
    'seconds-10': 10,
    'seconds-30': 30
};
export const sendMessagePayloadSchema = z
    .object({
    chatId: identifierSchema,
    text: z.string().max(MESSAGE_TEXT_LIMIT),
    replyToId: identifierSchema.optional(),
    /**
     * Ответ на историю: сообщение запомнит, к чему оно относится.
     *
     * Сервер проверяет, что отвечающий эту историю действительно видел, —
     * иначе поле не сохраняется, но само сообщение всё равно уходит: терять
     * набранный текст из-за истёкшей истории нельзя.
     */
    storyId: identifierSchema.optional(),
    /** Вложения уже загружены отдельным HTTP-запросом. */
    attachmentIds: z
        .array(identifierSchema)
        .max(MESSAGE_ATTACHMENT_LIMIT)
        .refine((ids) => new Set(ids).size === ids.length, 'Вложения не должны повторяться')
        .optional(),
    /** Визитка: кем поделились. Сообщение становится карточкой человека. */
    contactUserId: identifierSchema.optional(),
    /**
     * Через сколько исчезнет приложенное фото или видео.
     *
     * Принимается только в личном чате и только с одним изображением или
     * видео. В остальных случаях сервер отказывает, а не молчит: человек
     * нажал огонёк и обязан узнать, что тот не сработал.
     */
    ephemeral: ephemeralMediaSchema.optional()
})
    .strict();
/** Получатель открыл исчезающее фото — с этого мгновения идёт отсчёт. */
export const revealMessagePayloadSchema = z.object({ messageId: identifierSchema }).strict();
/** Получатель закрыл просмотр. Сервер не верит на слово: срок стережёт уборщик. */
export const burnMessagePayloadSchema = z.object({ messageId: identifierSchema }).strict();
export const editMessagePayloadSchema = z
    .object({
    messageId: identifierSchema,
    text: z.string().trim().min(1).max(MESSAGE_TEXT_LIMIT)
})
    .strict();
export const deleteMessagePayloadSchema = z.object({ messageId: identifierSchema }).strict();
export const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏', '🔥', '🎉'];
export const reactMessagePayloadSchema = z
    .object({ messageId: identifierSchema, emoji: z.enum(REACTION_EMOJIS) })
    .strict();
export const pinMessagePayloadSchema = z
    .object({ messageId: identifierSchema, pinned: z.boolean() })
    .strict();
const uniqueIdentifiers = (max) => z
    .array(identifierSchema)
    .min(1)
    .max(max)
    .refine((ids) => new Set(ids).size === ids.length, 'Идентификаторы не должны повторяться');
export const forwardMessagesPayloadSchema = z
    .object({
    messageIds: uniqueIdentifiers(50),
    targetChatIds: uniqueIdentifiers(20)
})
    .strict()
    .refine((value) => value.messageIds.length * value.targetChatIds.length <= 100, 'За один раз можно переслать не более 100 копий');
const pollOptionSchema = z.string().trim().min(1).max(100);
export const createPollPayloadSchema = z
    .object({
    chatId: identifierSchema,
    question: z.string().trim().min(1).max(300),
    options: z
        .array(pollOptionSchema)
        .min(2)
        .max(10)
        .refine((options) => new Set(options.map((option) => option.toLocaleLowerCase())).size === options.length, 'Варианты не должны повторяться'),
    allowsMultiple: z.boolean(),
    isAnonymous: z.boolean()
})
    .strict();
export const votePollPayloadSchema = z
    .object({
    pollId: identifierSchema,
    optionIds: z
        .array(identifierSchema)
        .max(10)
        .refine((ids) => new Set(ids).size === ids.length, 'Варианты не должны повторяться')
})
    .strict();
export const closePollPayloadSchema = z.object({ pollId: identifierSchema }).strict();
/**
 * Состояние приложения у человека: видно ли его сейчас на экране.
 *
 * Нужно ровно для одного решения — слать ли системное уведомление. Пока
 * приложение открыто, сообщение и так приезжает по этому же соединению, и
 * баннер поверх него только мешает.
 */
export const appStatePayloadSchema = z.object({ visible: z.boolean() }).strict();
export const typingPayloadSchema = z.object({ chatId: identifierSchema }).strict();
export const readPayloadSchema = z
    .object({ chatId: identifierSchema, seq: z.number().int().nonnegative() })
    .strict();
export const syncPullPayloadSchema = z
    .object({
    /*
     * Полсотни чатов за раз хватает с запасом: клиент спрашивает только
     * про открытые переписки, а их единицы. Прежние пятьсот при сотне
     * сообщений на каждую давали десятки тысяч записей со вложениями и
     * реакциями — одним запросом, и повторять его можно было без счёта.
     */
    cursors: z
        .array(z
        .object({
        chatId: identifierSchema,
        seq: z.number().int().nonnegative(),
        /**
         * Время последней успешной сверки этого чата — по часам
         * сервера, не клиента.
         *
         * По номеру находится только новое; правка и удаление
         * номер сохраняют. Пусто — значит сверки ещё не было, и
         * догон идёт по одному номеру, как раньше.
         */
        since: z.number().int().nonnegative().optional()
    })
        .strict())
        .max(50)
})
    .strict();
export const clientMessageSchema = z.discriminatedUnion('op', [
    z
        .object({ ...envelopeBase, op: z.literal('message.send'), payload: sendMessagePayloadSchema })
        .strict(),
    z
        .object({ ...envelopeBase, op: z.literal('message.edit'), payload: editMessagePayloadSchema })
        .strict(),
    z
        .object({
        ...envelopeBase,
        op: z.literal('message.delete'),
        payload: deleteMessagePayloadSchema
    })
        .strict(),
    z
        .object({ ...envelopeBase, op: z.literal('message.react'), payload: reactMessagePayloadSchema })
        .strict(),
    z
        .object({ ...envelopeBase, op: z.literal('message.pin'), payload: pinMessagePayloadSchema })
        .strict(),
    z
        .object({
        ...envelopeBase,
        op: z.literal('message.reveal'),
        payload: revealMessagePayloadSchema
    })
        .strict(),
    z
        .object({ ...envelopeBase, op: z.literal('message.burn'), payload: burnMessagePayloadSchema })
        .strict(),
    z
        .object({
        ...envelopeBase,
        op: z.literal('message.forward'),
        payload: forwardMessagesPayloadSchema
    })
        .strict(),
    z
        .object({ ...envelopeBase, op: z.literal('poll.create'), payload: createPollPayloadSchema })
        .strict(),
    z
        .object({ ...envelopeBase, op: z.literal('poll.vote'), payload: votePollPayloadSchema })
        .strict(),
    z
        .object({ ...envelopeBase, op: z.literal('poll.close'), payload: closePollPayloadSchema })
        .strict(),
    z
        .object({ ...envelopeBase, op: z.literal('chat.typing'), payload: typingPayloadSchema })
        .strict(),
    z.object({ ...envelopeBase, op: z.literal('chat.read'), payload: readPayloadSchema }).strict(),
    z
        .object({ ...envelopeBase, op: z.literal('sync.pull'), payload: syncPullPayloadSchema })
        .strict(),
    z
        .object({ ...envelopeBase, op: z.literal('app.state'), payload: appStatePayloadSchema })
        .strict(),
    z.object({ ...envelopeBase, op: z.literal('ping'), payload: z.object({}).strict() }).strict()
]);
/** Собрать конверт. Отметка времени и идентификатор проставляются здесь. */
export function envelope(op, payload, id) {
    return {
        op,
        id: id ?? crypto.randomUUID(),
        ts: Date.now(),
        payload
    };
}
//# sourceMappingURL=envelope.js.map